import { useCallback, useEffect, useRef } from 'react';
import { type FILTERS_OPEN } from '../constants';
import { type SetSearchParams } from './useExtendSearchParams';
import { useMutableObject } from './useMutableObject';
import { useSyncedState } from './useSyncedState';

// Можно дополнять по необходимости
export type SearchParamKey =
    // Строка, содержащая набор различный набор фильтров (цена, остатки, материал и т.д.)
    | 'filter'
    // Строка поискового текста
    | 'search'
    // Выбранные категории
    | 'categories'
    // Ключ активного (выбранного) табика
    | 'active-tab'
    // Выбранный статус
    | 'status'
    // Выбранный статус модерации (для товаров KIT Lite)
    | 'moderation_status'
    // Выбранные коллекции
    | 'collections'
    // Фильтр коллекций по статусу
    | 'filterCollectionStatus'
    // Фильтр коллекций по типу
    | 'filterCollectionType'
    // Фильтр коллекций по статусу
    | 'filterCollectionSearch'
    // Фильтр системных коллекций по типу
    | 'filterSystemCollectionType'
    // Флаг открытия фильтров
    | typeof FILTERS_OPEN
    // Поле для сортировки
    | 'sort_field'
    // Направление сортировки
    | 'sort_direction'
    // Тип промо (DISCOUNT | LADDER) для фильтра скидок
    | 'promo_type';

export type UsePropertyFromSearchParamsOptions = {
    /**
     * Задержка перед записью значения в URL.
     * Локальный `searchParam` обновляется сразу — input остаётся отзывчивым.
     * Пустое значение (clear/reset) пишется в URL сразу, отменяя отложенную запись.
     */
    debounceMs?: number;
};

/*
 * Хук для получения и заполнения значения поисковой строки
 * Требования
 * - Получать "searchParamKey" из query параметров
 * - Обновлять "searchParamKey" в query параметрах, при этом большую часть времени делать это через replace
 *   - Пользователь не хочет по истории гулять на каждый новый символ
 *   - Пользователь хочет гулять по истории запросов если они были скажем каждые 2 сек
 * @param searchParams результат выполнения хука useSearchParams
 * @param setSearchParams результат выполнения хука useSearchParams
 * @param searchParamKey ключ, по которому изменять и получать информацию в url
 * @param resetParams ключи для удаления из query параметров
 */
export const usePropertyFromSearchParams = <T extends string = string>(
    searchParams: URLSearchParams,
    setSearchParams: SetSearchParams,
    searchParamKey: SearchParamKey,
    resetParams: readonly string[] = [],
    options?: UsePropertyFromSearchParamsOptions
) => {
    const debounceMs = options?.debounceMs ?? 0;
    const ref = useMutableObject(resetParams);

    const [stateSearchParam, setStateSearchParam] = useSyncedState(searchParams.get(searchParamKey) ?? '');

    const refFreezePushHistory = useRef(false);
    const refTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const refDebounceTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (refDebounceTimerId.current) {
                clearTimeout(refDebounceTimerId.current);
            }

            if (refTimerId.current) {
                clearTimeout(refTimerId.current);
            }
        },
        []
    );

    const setSearchParamInUrl = useCallback(
        (value: T) => {
            setSearchParams((params) => {
                ref.current.forEach((key) => params.delete(key));

                if (value) {
                    params.set(searchParamKey, value);
                } else {
                    params.delete(searchParamKey);
                }

                return [
                    params,
                    {
                        replace: refFreezePushHistory.current,
                    },
                ];
            });

            refFreezePushHistory.current = true;
            refTimerId.current && clearTimeout(refTimerId.current);
            refTimerId.current = setTimeout(() => {
                refFreezePushHistory.current = false;
            }, 2000);
        },
        [setSearchParams, ref, searchParamKey]
    );

    const setSearchParam = useCallback(
        (value: T) => {
            setStateSearchParam(value);

            if (refDebounceTimerId.current) {
                clearTimeout(refDebounceTimerId.current);
                refDebounceTimerId.current = null;
            }

            // Clear/reset — сразу в URL, чтобы не перезаписать пустое значение отложенным вводом
            if (debounceMs <= 0 || value === '') {
                setSearchParamInUrl(value);

                return;
            }

            refDebounceTimerId.current = setTimeout(() => {
                refDebounceTimerId.current = null;
                setSearchParamInUrl(value);
            }, debounceMs);
        },
        [debounceMs, setSearchParamInUrl, setStateSearchParam]
    );

    return {
        searchParam: stateSearchParam as T | '',
        setSearchParam,
    };
};
